import React from 'react';
import { Dimensions, Platform } from 'react-native';
import {
  Checkbox,
  CheckboxIndicator,
  CheckboxIcon,
  CheckIcon,
  HStack,
  Text,
  ScrollView,
} from '@gluestack-ui/themed';
import { useDialog } from '../context/DialogContext';
import { useAuthStore } from '../auth/store';
import HtmlText from '../components/HtmlText';
import whatsNew from './whats-new.json';
import { updateSeenEntry, readWhatsNewState } from './storage';

interface WhatsNewMessage {
  id: string;
  title?: string;
  roles: string[];
  startAt: string;
  endAt: string;
  html: string;
  priority?: number;
  platforms?: string[];
  maxShows?: number;
}

interface WhatsNewConfig {
  version: number;
  messages: WhatsNewMessage[];
}

const isNowBetween = (startIso: string, endIso: string) => {
  const now = Date.now();
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  return Number.isFinite(start) && Number.isFinite(end) && now >= start && now <= end;
};

const pickMessageForUser = async (
  userId: string,
  role: string,
  config: WhatsNewConfig,
  currentPlatform: 'ios' | 'android' | 'web',
): Promise<WhatsNewMessage | null> => {
  const state = await readWhatsNewState(userId);
  const candidates = config.messages
    .filter((m) => m.roles.includes(role))
    .filter((m) => isNowBetween(m.startAt, m.endAt))
    .filter((m) => {
      const platforms = m.platforms || ['all'];
      return platforms.includes('all') || platforms.includes(currentPlatform);
    })
    .sort((a, b) => {
      const ap = a.priority ?? 0;
      const bp = b.priority ?? 0;
      if (ap !== bp) return bp - ap;
      return Date.parse(b.startAt) - Date.parse(a.startAt);
    });

  for (const m of candidates) {
    const seen = state.seen[m.id];
    if (!seen || seen.showAgain || !seen.dismissed) {
      return m;
    }
  }
  return null;
};

export const WhatsNewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showDialog, hideDialog } = useDialog();
  const user = useAuthStore((s) => s.user);
  const isInitializing = useAuthStore((s) => s.isInitializing);

  const [hasShownThisSession, setHasShownThisSession] = React.useState(false);

  React.useEffect(() => {
    if (isInitializing || !user || hasShownThisSession) {
      return () => {};
    }

    let cancelled = false;
    const config = whatsNew as WhatsNewConfig;

    (async () => {
      const message = await pickMessageForUser(
        String(user.id || user.username),
        user.role,
        config,
        Platform.OS as 'ios' | 'android' | 'web',
      );
      if (!message || cancelled) return;

      let showAgainChecked = false;

      const onClose = async () => {
        await updateSeenEntry(String(user.id || user.username), message.id, (prev) => ({
          ...prev,
          lastSeenAt: new Date().toISOString(),
          showAgain: showAgainChecked,
          dismissed: !showAgainChecked,
          showCount: (prev.showCount || 0) + 1,
        }));
        hideDialog();
      };

      // Compute responsive dimensions to ensure long content scrolls
      const { width: windowWidth, height: windowHeight } = Dimensions.get('window');
      const maxBodyHeight = Math.max(260, Math.round(windowHeight * 0.6));
      const contentWidth = Math.min(800, Math.round(windowWidth * 0.85));

      showDialog({
        size: 'lg',
        title: message.title || "What's New",
        message: '',
        buttons: [
          {
            text: 'Close',
            action: 'primary',
            onPress: onClose,
          },
        ],
        children: (
          <>
            <ScrollView maxHeight={maxBodyHeight} w="100%">
              <HtmlText
                content={message.html}
                width={contentWidth}
                numberOfLines={0}
                fontSize={14}
                contentStyle={{}}
                applySubmissionFormatting={false}
              />
            </ScrollView>
            <HStack mt={12} space="sm" alignItems="center">
              <Checkbox
                value="showAgain"
                onChange={(v: boolean) => {
                  showAgainChecked = v;
                }}
              >
                <CheckboxIndicator>
                  <CheckboxIcon as={CheckIcon} />
                </CheckboxIndicator>
              </Checkbox>
              <Text size="xs">Show this again</Text>
            </HStack>
          </>
        ),
      });

      setHasShownThisSession(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line consistent-return
  }, [isInitializing, user, hasShownThisSession, showDialog, hideDialog]);

  return children;
};

export default WhatsNewProvider;
